import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle, Clock } from "lucide-react";

// Rajasthan Map Component - Using official Wikimedia Commons outline SVG
const RajasthanMap = () => (
  <div className="w-full h-full flex items-center justify-center">
    <img 
      src="/images/rajasthan-outline.svg" 
      alt="Rajasthan State Map"
      className="w-full h-full object-contain opacity-60"
      style={{ filter: 'hue-rotate(140deg) saturate(0.5)' }}
    />
  </div>
);

// West Bengal Map Component - Using official Wikimedia Commons outline SVG
const WestBengalMap = () => (
  <div className="w-full h-full flex items-center justify-center">
    <img 
      src="/images/westbengal-outline.svg" 
      alt="West Bengal State Map"
      className="w-full h-full object-contain opacity-60"
      style={{ filter: 'hue-rotate(40deg) saturate(0.8) brightness(1.1)' }}
    />
  </div>
);

const FeaturedStates = ({ onOpenInquiry }) => {
  return (
    <section className="py-12 md:py-16 bg-white" data-testid="featured-states-section">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="max-w-5xl mx-auto">
          
          {/* Section Header */}
          <div className="text-center mb-10">
            <h2 className="font-display text-2xl md:text-3xl font-bold text-[#4A4A4A] mb-3">
              Featured State Implementations
            </h2>
          </div>

          <div className="space-y-6">
            
            {/* RAJASTHAN - Established Model (Green) */}
            <Card className="p-0 overflow-hidden border-0 shadow-sm" data-testid="rajasthan-card">
              <div className="flex flex-col md:flex-row bg-[#F2F7F4]">
                {/* Left Accent Bar */}
                <div className="hidden md:block w-1 bg-[#1F7A5A]"></div>
                
                {/* State Map */}
                <div className="w-full md:w-48 p-4 flex items-center justify-center">
                  <div className="w-32 h-32">
                    <RajasthanMap />
                  </div>
                </div>
                
                {/* Content */}
                <div className="flex-1 p-6 relative">
                  {/* Top-right Tag */}
                  <span className="absolute top-4 right-4 inline-flex items-center gap-1.5 text-[10px] font-semibold text-[#1F7A5A] bg-[#1F7A5A]/10 px-2.5 py-1 rounded-full">
                    <CheckCircle className="w-3 h-3" />
                    Established Model
                  </span>

                  {/* Title */}
                  <h3 className="font-display text-lg font-bold text-[#145A43] mb-1 pr-32">
                    Rajasthan Care-Leaver Aftercare Program
                  </h3>
                  <p className="text-sm text-[#1F7A5A] font-medium mb-3">
                    Three-Year Implementation Track Record
                  </p>

                  {/* Co-funded by */}
                  <p className="text-xs text-[#6B7280] mb-4">
                    <span className="font-medium">Co-funded:</span> UNICEF · Azim Premji Foundation · Government of Rajasthan
                  </p>

                  {/* Strengths */}
                  <div className="grid grid-cols-2 gap-2 mb-5">
                    <div className="flex items-center gap-2 text-sm text-[#4A4A4A]">
                      <CheckCircle className="w-4 h-4 text-[#1F7A5A] flex-shrink-0" />
                      <span>Proven continuity</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-[#4A4A4A]">
                      <CheckCircle className="w-4 h-4 text-[#1F7A5A] flex-shrink-0" />
                      <span>Structured reporting</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-[#4A4A4A]">
                      <CheckCircle className="w-4 h-4 text-[#1F7A5A] flex-shrink-0" />
                      <span>State alignment</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-[#4A4A4A]">
                      <CheckCircle className="w-4 h-4 text-[#1F7A5A] flex-shrink-0" />
                      <span>Committee-ready governance</span>
                    </div>
                  </div>

                  {/* Impact Stat */}
                  <div className="bg-[#1F7A5A]/10 rounded-lg p-3 mb-4">
                    <p className="text-xl font-bold text-[#1F7A5A]">500+ <span className="text-sm font-normal text-[#4A4A4A]">care-leavers supported</span></p>
                  </div>

                  {/* CTA with Microcopy */}
                  <div>
                    <Button
                      onClick={() => onOpenInquiry && onOpenInquiry({ 
                        type: "rajasthan", 
                        title: "Review the Rajasthan Model",
                        geography: "Rajasthan"
                      })}
                      className="bg-[#1F7A5A] hover:bg-[#145A43] text-white font-medium text-sm h-10 px-5 transition-colors"
                      data-testid="rajasthan-cta"
                    >
                      Review the Rajasthan Model
                    </Button>
                    <p className="text-[10px] text-[#6B7280] mt-2">
                      Explore the established two-year implementation framework.
                    </p>
                  </div>
                </div>
              </div>
            </Card>

            {/* WEST BENGAL - Current Deployment (Yellow) */}
            <Card className="p-0 overflow-hidden border-0 shadow-sm" data-testid="west-bengal-card">
              <div className="flex flex-col md:flex-row bg-[#FFF8E6]">
                {/* Left Accent Bar */}
                <div className="hidden md:block w-1 bg-[#F4C430]"></div>
                
                {/* State Map */}
                <div className="w-full md:w-48 p-4 flex items-center justify-center">
                  <div className="w-24 h-40">
                    <WestBengalMap />
                  </div>
                </div>
                
                {/* Content */}
                <div className="flex-1 p-6 relative">
                  {/* Top-right Tag */}
                  <span className="absolute top-4 right-4 inline-flex items-center gap-1.5 text-[10px] font-semibold text-[#B8860B] bg-[#F4C430]/25 px-2.5 py-1 rounded-full">
                    <Clock className="w-3 h-3" />
                    Current Deployment Window
                  </span>

                  {/* Title */}
                  <h3 className="font-display text-lg font-bold text-[#2F2F2F] mb-1 pr-40">
                    West Bengal Care-Leaver Aftercare Initiative
                  </h3>
                  <p className="text-sm text-[#B8860B] font-medium mb-3">
                    Open for Current CSR Allocation Cycle
                  </p>

                  {/* Co-funded by */}
                  <p className="text-xs text-[#6B7280] mb-4">
                    <span className="font-medium">Co-funded:</span> Government of West Bengal · UNICEF
                  </p>

                  {/* Time-sensitive points */}
                  <div className="grid grid-cols-2 gap-2 mb-5">
                    <div className="flex items-center gap-2 text-sm text-[#4A4A4A]">
                      <Clock className="w-4 h-4 text-[#B8860B] flex-shrink-0" />
                      <span>Cohort identified</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-[#4A4A4A]">
                      <Clock className="w-4 h-4 text-[#B8860B] flex-shrink-0" />
                      <span>Co-funding in place</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-[#4A4A4A]">
                      <Clock className="w-4 h-4 text-[#B8860B] flex-shrink-0" />
                      <span>4–6 week deployment</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-[#4A4A4A]">
                      <Clock className="w-4 h-4 text-[#B8860B] flex-shrink-0" />
                      <span>Aligned to current allocation cycles</span>
                    </div>
                  </div>

                  {/* CTA with Yellow Pill and Microcopy */}
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-[9px] font-semibold text-[#B8860B] bg-[#F4C430]/30 px-2 py-0.5 rounded-full">
                        Current Cycle
                      </span>
                    </div>
                    <Button
                      onClick={() => onOpenInquiry && onOpenInquiry({ 
                        type: "westbengal", 
                        title: "Discuss West Bengal Participation",
                        geography: "West Bengal"
                      })}
                      className="bg-[#1F7A5A] hover:bg-[#145A43] text-white font-medium text-sm h-10 px-5 transition-colors"
                      data-testid="west-bengal-cta"
                    >
                      Discuss West Bengal Participation
                    </Button>
                    <p className="text-[10px] text-[#6B7280] mt-2">
                      Suitable for current-cycle CSR allocations.
                    </p>
                  </div>
                </div>
              </div>
            </Card>

          </div>
        </div>
      </div>
    </section>
  );
};

export default FeaturedStates;
